// ════════════════════════════════════════════════════════════════════════
// TCC PUSH — storefront script: registers the service worker, handles
// push subscription/unsubscription, and wires up the "Enable Order
// Reminders" button.
//
// v2: migrated the subscribe/unsubscribe backend off Google Apps Script
// onto the Cloudflare Worker (tcc-kitchen-worker), matching the rest of
// TCC's infrastructure. New VAPID keypair issued as part of this move —
// existing subscribers (if any) will need to click "Enable Order
// Reminders" again, since browsers bind a push subscription to the
// specific applicationServerKey used at signup time.
// ════════════════════════════════════════════════════════════════════════

var TCC_VAPID_PUBLIC_KEY = 'BKmtJJRdzBbPbTeCBvTp2J1kHCYS0gTxCH-mhpIxD9qg_9VbFpipPWWObNEykAo7e5Sb4T4q7_r5mmTojU-Wi0I';
var WORKER_URL = 'https://tcc-kitchen-worker.long-rice-fd6e.workers.dev';
var PUSH_SUBSCRIBE_URL = WORKER_URL + '/api/push/subscribe';
var PUSH_UNSUBSCRIBE_URL = WORKER_URL + '/api/push/unsubscribe';

// Set from theme-head-snippet.liquid to the real asset CDN URL for
// tcc-sw.js once it's uploaded as a theme asset (Phase 5).
var TCC_SW_URL = window.TCC_SW_URL || '/tcc-sw.js';

// Converts the VAPID public key (base64url) into the Uint8Array format
// the Push API requires for applicationServerKey.
function tccUrlBase64ToUint8Array_(base64String) {
  var padding = '='.repeat((4 - base64String.length % 4) % 4);
  var base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  var rawData = window.atob(base64);
  var outputArray = new Uint8Array(rawData.length);
  for (var i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Waits for THIS SPECIFIC registration's worker to become active — not
// the same as navigator.serviceWorker.ready, which requires the worker's
// scope to cover the current page. Since tcc-sw.js is hosted under
// /cdn/.../assets/ (a Shopify theme asset path), its scope is restricted
// to that directory and will never cover storefront pages like
// /pages/choose-your-dishes. Push subscription only needs THIS
// registration to have an active worker — it does NOT need to control
// the current page — so waiting on the registration directly (rather
// than .ready) is the correct, working approach here.
function tccWaitForActive_(registration) {
  return new Promise(function(resolve, reject) {
    if (registration.active) {
      resolve(registration);
      return;
    }
    var worker = registration.installing || registration.waiting;
    if (!worker) {
      reject(new Error('No installing, waiting, or active service worker found on this registration.'));
      return;
    }
    worker.addEventListener('statechange', function() {
      if (worker.state === 'activated') {
        resolve(registration);
      } else if (worker.state === 'redundant') {
        reject(new Error('Service worker became redundant before activating.'));
      }
    });
  });
}

function tccRegisterServiceWorker_() {
  if (!('serviceWorker' in navigator)) {
    return Promise.reject(new Error('Service workers not supported in this browser.'));
  }
  return navigator.serviceWorker.register(TCC_SW_URL);
}

function tccGetCustomerEmail_() {
  return (typeof window.TCC_CUSTOMER_EMAIL === 'string' && window.TCC_CUSTOMER_EMAIL) ? window.TCC_CUSTOMER_EMAIL : '';
}

// --- PLATFORM DETECTION ------------------------------------------------
// iOS Safari (16.4+) only supports Web Push for a PWA that's been
// installed via "Add to Home Screen" AND is currently running in that
// standalone mode — a regular Safari tab can never receive push,
// permission prompt or not. Clicking the normal subscribe button in a
// plain Safari tab used to fail with a generic, confusing error. We now
// detect this case up front and show accurate instructions instead.
function tccIsIOS_() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function tccIsStandalone_() {
  return window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
}

function tccShowIOSInstallInstructions_() {
  if (document.getElementById('tcc-ios-instructions-backdrop')) {
    document.getElementById('tcc-ios-instructions-backdrop').style.display = 'flex';
    return;
  }
  var backdrop = document.createElement('div');
  backdrop.id = 'tcc-ios-instructions-backdrop';
  backdrop.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;z-index:100000;padding:20px;';
  backdrop.innerHTML =
    '<div style="background:#fff;border-radius:14px;max-width:360px;width:100%;padding:24px;font-family:\'DM Sans\',sans-serif;box-shadow:0 20px 60px rgba(0,0,0,.3);">' +
      '<div style="font-size:15px;font-weight:700;color:#1A0F00;margin-bottom:14px;">Enable reminders on iPhone</div>' +
      '<ol style="margin:0 0 18px;padding-left:20px;font-size:13.5px;color:#3D1F1F;line-height:1.9;">' +
        '<li>Tap the <strong>Share</strong> button <span style="font-size:15px;">&#x2B06;&#xFE0F;</span> at the bottom of Safari</li>' +
        '<li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>' +
        '<li>Close Safari, then open the <strong>new TCC icon</strong> from your Home Screen</li>' +
        '<li>Come back to this page and tap <strong>"Enable Order Reminders"</strong> again</li>' +
      '</ol>' +
      '<button id="tcc-ios-instructions-close" style="width:100%;padding:11px;background:#8B1A1A;color:#fff;border:none;border-radius:8px;font-weight:700;font-size:13.5px;cursor:pointer;">Got it</button>' +
    '</div>';
  document.body.appendChild(backdrop);
  document.getElementById('tcc-ios-instructions-close').addEventListener('click', function() {
    backdrop.style.display = 'none';
  });
  backdrop.addEventListener('click', function(e) { if (e.target === backdrop) backdrop.style.display = 'none'; });
}

// --- REAL APP INSTALL (Android / Desktop Chrome & Edge) --------------
// Chrome-family browsers fire 'beforeinstallprompt' for any site with a
// valid manifest.json + registered service worker — both already in
// place here. Capturing this event and calling .prompt() later shows
// the browser's own native "Install this app?" dialog, the same UX as
// installing any app from a store — a genuine install, not just a
// bookmark. iOS Safari has no equivalent API at all (Apple's decision,
// not something we can work around), so it always falls back to the
// manual Add to Home Screen instructions.
var _tccDeferredInstallPrompt = null;

window.addEventListener('beforeinstallprompt', function(e) {
  e.preventDefault();
  _tccDeferredInstallPrompt = e;
  document.querySelectorAll('.tcc-install-app-btn').forEach(function(btn) {
    btn.hidden = false;
  });
});

window.addEventListener('appinstalled', function() {
  _tccDeferredInstallPrompt = null;
  document.querySelectorAll('.tcc-install-app-btn').forEach(function(btn) {
    btn.textContent = '\u2713 App Installed';
    btn.disabled = true;
  });
});

function tccInstallApp() {
  if (tccIsIOS_()) {
    tccShowIOSInstallInstructions_();
    return;
  }
  if (!_tccDeferredInstallPrompt) {
    // Already installed, already dismissed this session, or a browser
    // (e.g. Firefox) that doesn't support the install-prompt API at all.
    alert('Look for an install icon in your browser\'s address bar, or check your browser menu for "Install app" / "Add to Home screen." If you\'ve already installed it, you\'re all set!');
    return;
  }
  _tccDeferredInstallPrompt.prompt();
  _tccDeferredInstallPrompt.userChoice.then(function() {
    _tccDeferredInstallPrompt = null;
  });
}

function tccInitInstallButtons_() {
  var isStandaloneAlready = tccIsStandalone_();
  document.querySelectorAll('.tcc-install-app-btn').forEach(function(btn) {
    if (isStandaloneAlready) {
      btn.textContent = '\u2713 App Installed';
      btn.disabled = true;
      return;
    }
    // On iOS, the button is always usable (opens instructions). On
    // other platforms, stay hidden until beforeinstallprompt actually
    // fires above — clicking too early would have nothing to prompt.
    if (tccIsIOS_()) btn.hidden = false;
    btn.addEventListener('click', tccInstallApp);
  });
}

function tccSubscribeToPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    alert('Push notifications are not supported in this browser.');
    return Promise.reject(new Error('Push not supported'));
  }

  return Notification.requestPermission().then(function(permission) {
    if (permission !== 'granted') {
      throw new Error('Notification permission was not granted.');
    }
    return tccRegisterServiceWorker_();
  }).then(function(registration) {
    return Promise.race([
      tccWaitForActive_(registration),
      new Promise(function(_, reject) {
        setTimeout(function() {
          reject(new Error('Timed out waiting for the service worker to activate (15s).'));
        }, 15000);
      })
    ]);
  }).then(function(registration) {
    return registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: tccUrlBase64ToUint8Array_(TCC_VAPID_PUBLIC_KEY)
    });
  }).then(function(subscription) {
    var subJson = subscription.toJSON();
    return fetch(PUSH_SUBSCRIBE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' }, // avoids CORS preflight
      body: JSON.stringify({
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        customer_email: tccGetCustomerEmail_()
      })
    }).then(function(res) { return res.json(); });
  }).then(function(result) {
    tccUpdateButtonState_(true);
    return result;
  }).catch(function(err) {
    console.error('TCC push subscribe failed:', err);
    alert('Could not enable notifications: ' + err.message);
    throw err;
  });
}

function tccUnsubscribeFromPush() {
  return tccRegisterServiceWorker_().then(function(registration) {
    return registration.pushManager.getSubscription();
  }).then(function(subscription) {
    if (!subscription) return null;
    var endpoint = subscription.endpoint;
    return subscription.unsubscribe().then(function() {
      return fetch(PUSH_UNSUBSCRIBE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ endpoint: endpoint })
      }).then(function(res) { return res.json(); });
    });
  }).then(function(result) {
    tccUpdateButtonState_(false);
    return result;
  }).catch(function(err) {
    console.error('TCC push unsubscribe failed:', err);
    throw err;
  });
}

function tccUpdateButtonState_(isSubscribed) {
  var btn = document.getElementById('tcc-enable-push-btn');
  if (!btn) return;
  if (isSubscribed) {
    btn.textContent = 'Order Reminders: On';
    btn.setAttribute('data-subscribed', 'true');
  } else {
    btn.textContent = 'Enable Order Reminders';
    btn.setAttribute('data-subscribed', 'false');
  }
}

function tccInitPushButton_() {
  var btn = document.getElementById('tcc-enable-push-btn');
  if (!btn) return;

  // iOS Safari not yet installed to Home Screen — Web Push is
  // structurally impossible here (not a permission issue, a platform
  // one), so show the install steps instead of a button that will just
  // fail with a confusing error when clicked.
  if (tccIsIOS_() && !tccIsStandalone_()) {
    btn.textContent = '\uD83D\uDCF2 Add to Home Screen to Enable';
    btn.addEventListener('click', tccShowIOSInstallInstructions_);
    return;
  }

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    btn.style.display = 'none';
    return;
  }

  if (Notification.permission === 'granted') {
    tccRegisterServiceWorker_().then(function(registration) {
      return registration.pushManager.getSubscription();
    }).then(function(sub) {
      tccUpdateButtonState_(!!sub);
    }).catch(function() { /* no-op */ });
  }

  btn.addEventListener('click', function() {
    var isSubscribed = btn.getAttribute('data-subscribed') === 'true';
    if (isSubscribed) {
      tccUnsubscribeFromPush();
    } else {
      tccSubscribeToPush();
    }
  });
}

document.addEventListener('DOMContentLoaded', function() {
  tccInitPushButton_();
  tccInitInstallButtons_();
});